import { useMemo } from 'react'
import { appPath, sgBaseUrl } from '@fs/zion-config'
import { useUser } from '@fs/zion-user'
import { useQuery } from '@tanstack/react-query'
import axios from '../utils/giveAxios'

const groupReportsUrl = `${sgBaseUrl}/service/records/volunteer/orchestration/user/authorization/reports/groups`
const whitelistUrl = `${appPath}/reports-whitelist`

const whiteListKey = 'reports-whitelist'
const groupReportsKey = 'reports-group-reports'

export default function useReportsAccess() {
  const { cisId, signedIn, userLoading } = useUser()

  const {
    data: whitelistData,
    isLoading: whitelistLoading,
    isFetching: whitelistFetching,
    error: whitelistError,
  } = useQuery({
    queryKey: [whiteListKey, cisId],
    queryFn: () =>
      axios.get(whitelistUrl, {
        headers: { 'x-fs-feature-tag': 'a02j000000BZ3ZTAA1' },
      }),
    cacheTime: 1000 * 60 * 60,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: signedIn && !userLoading,
  })

  const {
    data: groupReportsData,
    isLoading: groupReportsLoading,
    isFetching: groupReportsFetching,
    error: groupReportsError,
  } = useQuery({
    queryKey: [groupReportsKey],
    queryFn: () => axios.get(groupReportsUrl),
    cacheTime: 1000 * 60 * 60,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: signedIn && !userLoading,
  })

  const { authorized, loading, units } = useMemo(() => {
    // If user is not signed in, return not authorized immediately
    if (!signedIn && !userLoading) {
      return { authorized: false, units: [], loading: false }
    }

    // If any loading or fetching state is true, return loading
    if (userLoading || whitelistLoading || whitelistFetching || groupReportsLoading || groupReportsFetching) {
      return { authorized: false, units: [], loading: true }
    }

    // If there are errors and no data, return not authorized
    const hasAnyWhitelistData = whitelistData?.data?.whitelist?.length > 0
    // At this point, we know signedIn is true and all loading/fetching is false
    // Check if either data source has results (must check length for arrays)
    const hasGroupReportsData = groupReportsData?.data?.length > 0

    if ((whitelistError || groupReportsError) && !hasAnyWhitelistData && !hasGroupReportsData) {
      return { authorized: false, units: [], loading: false }
    }

    // For whitelist data, only attempt to find an entry if we have a valid cisId
    const hasValidCisId = cisId != null
    const userWhitelistEntry = hasValidCisId
      ? whitelistData?.data?.whitelist?.find((entry) => entry.cisId === cisId)
      : undefined
    const hasWhitelistData = userWhitelistEntry?.units?.length > 0

    if (hasWhitelistData || hasGroupReportsData) {
      // Prioritize groupReportsData if it has data, otherwise use user's whitelist units
      const authorizedUnits = hasGroupReportsData ? groupReportsData.data : userWhitelistEntry.units
      return {
        authorized: true,
        units: authorizedUnits,
        loading: false,
      }
    }
    return { authorized: false, units: [], loading: false }
  }, [
    cisId,
    signedIn,
    userLoading,
    whitelistLoading,
    whitelistFetching,
    groupReportsLoading,
    groupReportsFetching,
    whitelistData,
    groupReportsData,
    whitelistError,
    groupReportsError,
  ])

  return {
    authorized,
    data: units,
    error: groupReportsError || whitelistError,
    loading,
  }
}
